<script lang="ts">
  import { isMobileDevice, isLandscape } from '$lib/stores/device';
  import { t } from '$lib/i18n';
  import { RotateCcwSquare } from '@lucide/svelte';

  /*
  Enforce portrait orientation on mobile clients (which are locked to catalog mode).
  Two layers:
   - Best-effort native lock via the Screen Orientation API. This only succeeds on
     browsers that support it while in fullscreen/PWA context (mainly Android Chrome);
     it throws elsewhere (e.g. iOS Safari), which we deliberately ignore.
   - A full-screen overlay prompting the user to rotate back to portrait — the reliable
     fallback that works everywhere, shown whenever a mobile device is in landscape.
  */
  $effect(() => {
    if (!$isMobileDevice) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const orientation = (screen as any)?.orientation;
    if (orientation && typeof orientation.lock === 'function') {
      // May reject (unsupported / not fullscreen); the overlay covers those cases.
      Promise.resolve(orientation.lock('portrait')).catch(() => {});
    }
  });

  const showOverlay = $derived($isMobileDevice && $isLandscape);
</script>

{#if showOverlay}
  <div class="orientation-overlay">
    <RotateCcwSquare class="orientation-icon" />
    <h2 class="orientation-title">{$t('orientation.title')}</h2>
    <p class="orientation-message">{$t('orientation.message')}</p>
  </div>
{/if}

<style>
  .orientation-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    text-align: center;
    background: white;
    color: #1f2937;
  }

  :global(.orientation-icon) {
    width: 4rem;
    height: 4rem;
    color: #2563eb;
  }

  .orientation-title {
    font-size: 1.25rem;
    font-weight: 700;
  }

  .orientation-message {
    max-width: 28rem;
    font-size: 0.95rem;
    color: #4b5563;
  }
</style>
